import {
	ICurrencies,
	IItemForGetShortInfo,
	IRestaurantBankSettingsAcquirer,
	IRestaurantsLanguages,
	IUpdateRestaurant,
	TPosSystemId,
	TAdditionalApplications,
	TMenuContentVersion,
	CardBrands,
	TTypeOfServiceProvided,
	TUseTipsByDefault,
	TTlsCertificateLevel,
	ISupplier,
} from 'types';
import { createContext } from 'react';

export type TEditRestaurantVisibleForm =
	| ''
	| 'mainForm'
	| 'mainFormForClientManager'
	| 'localizationForm'
	| 'styleForm'
	| 'devicesForm'
	| 'watchesForm'
	| 'reportsForm'
	| 'bankSettingsForm'
	| 'androidWatchesForm'
	| 'nfcCardsForm';
export interface IFormMccCard {
	id: string;
	brand: CardBrands | null;
	mcc: Nullable<number>;
}
export interface IExtraPaymentType {
	id: string;
	brand: CardBrands | null;
	paymentTypeId: string | null;
}
export type TTypeOfEditRestaurantModalWindow =
	| null
	| 'toConfirmFieldsClearing'
	| 'toConfirmSubmit'
	| 'success'
	| 'error'
	| 'tlsFileSizeExceeded'
	| 'tlsFileTypeError'
	| 'charityLogoFileSizeExceeded'
	| 'charityLogoFileTypeError';

export interface IInitialFormData {
	name: string;
	shortName: string;
	IDSelectedCustomer: Nullable<string>;
	clientType: Nullable<TTypeOfServiceProvided>;
	IDSelectedCountry: Nullable<string>;
	IDSelectedCity: Nullable<string>;
	address: string;
	selectedCurrencySymbol: Nullable<string>;
	masterPhoneNumber: string;
	email: string;
	code: Nullable<string>;
	additionalApplications: TAdditionalApplications[];
	vivaWalletIsv: {
		sourceId: string;
		isvSourceId: string;
		isvPercent: Nullable<number>;
		isvFixedFeeAmount: Nullable<number>;
		isvFixedFeeThreshold: Nullable<number>;
		pinCode: string;
		franchiseFeeSupported: boolean;
		franchisePercent: Nullable<number>;
		franchiseFixedFeeAmount: Nullable<number>;
		franchiseFixedFeeThreshold: Nullable<number>;
	};
	priceType: Nullable<string>;
	supplier: ISupplier;
	isOpenPriceServiceCharge: boolean;
	openPricePercent: Nullable<number>;
	isUsedCustomerPriceType: boolean;
	loyalty: string;
	isUsedCustomerTips: boolean;
	tips0: null | number;
	tips1: null | number;
	tips2: null | number;
	tips3: null | number;
	defaultTips: TUseTipsByDefault;
	tipsCommissionPercent: Nullable<number>;
	tipsCommissionMaxValue: Nullable<number>;
	isCharityEnabled: boolean;
	charityName: string;
	charityLogoPath: Nullable<string>;
	charityLogoFileId: Nullable<string>;
	charityAmount0: null | number;
	charityAmount1: null | number;
	charityAmount2: null | number;
	charityAmount3: null | number;
	defaultCharityAmountIndex: Nullable<number>;
	useKmTariff: boolean;
	isShowLoyaltyProgramButtons: boolean;
	baseMcc: Nullable<number>;
	mainMccCardBrand: CardBrands | null;
	mainMccCardMcc: Nullable<number>;
	mccCards: IFormMccCard[];
	useCustomerMcc: boolean;
	IDDefaultLanguage: Nullable<string>;
	IDsLanguages: string[];
	restrictionQrValue: string;
	usePosPhotos: boolean;
	enableStopListJob: boolean;
	useCustomerStopListSyncInterval: boolean;
	stopListSyncIntervalMinutes: Nullable<number>;
	updateOfTheLauncher: boolean;
	cardAcceptorId: string;
	cardAcceptorName: string;
	terminalId: string;
	vat: string;
	timeBlockChildAccess: string;
	IDPosSystem: Nullable<TPosSystemId>;
	launcherSettingFilePath: string | null;
	downloadAppAndroidUrl: string;
	downloadAppIosUrl: string;
	useMenuVersion: TMenuContentVersion;
	useContentVersion: TMenuContentVersion;
	restaurantMenuVersion: string;
	restaurantContentVersion: string;
	useTlsCertificate: boolean;
	tlsCertificateLevel: Nullable<TTlsCertificateLevel>;
	tlsCertificateName: Nullable<string>;
	tlsCertificateFilePath: Nullable<string>;
	tlsCertificateFileId: Nullable<string>;
	vatId: string;
	kmVatId: string;
	bpaSetId: Nullable<string>;
	isApplicableForVat: boolean;
	sendDeviceUnavailabilityAlerts: boolean;
	paymentTypeId: Nullable<string>;
	extraPaymentTypes: IExtraPaymentType[];
}

export interface IEditRestaurantState {
	needRefetchData: boolean;
	initialFormData: IInitialFormData | null;
	visibleForm: TEditRestaurantVisibleForm;
	restaurantId: string;
	name: string;
	shortName: string;
	fetchedCustomers: IItemForGetShortInfo[];
	IDSelectedCustomer: Nullable<string>;
	clientType: Nullable<TTypeOfServiceProvided>;
	fetchedCountries: IItemForGetShortInfo[];
	IDSelectedCountry: Nullable<string>;
	fetchedCities: IItemForGetShortInfo[];
	IDSelectedCity: Nullable<string>;
	address: string;
	fetchedCurrencies: ICurrencies[];
	selectedCurrencySymbol: Nullable<string>;
	masterPhoneNumber: string;
	email: string;
	code: Nullable<string>;
	additionalApplications: TAdditionalApplications[];
	vivaWalletIsv: {
		sourceId: string;
		isvSourceId: string;
		isvPercent: Nullable<number>;
		isvFixedFeeAmount: Nullable<number>;
		isvFixedFeeThreshold: Nullable<number>;
		pinCode: string;
		franchiseFeeSupported: boolean;
		franchisePercent: Nullable<number>;
		franchiseFixedFeeAmount: Nullable<number>;
		franchiseFixedFeeThreshold: Nullable<number>;
	};
	priceType: Nullable<string>;
	supplier: ISupplier;
	isOpenPriceServiceCharge: boolean;
	openPricePercent: Nullable<number>;
	isUsedCustomerPriceType: boolean;
	loyalty: string;
	isUsedCustomerTips: boolean;
	tips0: null | number;
	tips1: null | number;
	tips2: null | number;
	tips3: null | number;
	defaultTips: TUseTipsByDefault;
	tipsCommissionPercent: Nullable<number>;
	tipsCommissionMaxValue: Nullable<number>;
	isCharityEnabled: boolean;
	charityName: string;
	charityLogoPath: Nullable<string>;
	charityLogoFileId: Nullable<string>;
	charityAmount0: null | number;
	charityAmount1: null | number;
	charityAmount2: null | number;
	charityAmount3: null | number;
	defaultCharityAmountIndex: Nullable<number>;
	useKmTariff: boolean;
	isShowCustomerLoyaltyProgramButtons: boolean;
	isShowLoyaltyProgramButtons: boolean;
	baseMcc: Nullable<number>;
	mainMccCardBrand: CardBrands | null;
	mainMccCardMcc: Nullable<number>;
	mccCards: IFormMccCard[];
	useCustomerMcc: boolean;
	downloadAppAndroidUrl: string;
	downloadAppIosUrl: string;
	fetchedLanguages: IItemForGetShortInfo[];
	IDDefaultLanguage: Nullable<string>;
	IDsLanguages: string[];
	restaurantLanguages: IRestaurantsLanguages[];
	restrictionQrValue: string;
	usePosPhotos: boolean;
	enableStopListJob: boolean;
	useCustomerStopListSyncInterval: boolean;
	stopListSyncIntervalMinutes: Nullable<number>;
	updateOfTheLauncher: boolean;
	cardAcceptorId: string;
	cardAcceptorName: string;
	terminalId: string;
	vat: string;
	timeBlockChildAccess: string;
	IDPosSystem: Nullable<TPosSystemId>;
	fileIdForLauncherSettings: string | null;
	launcherSettingFilePath: string | null;
	fetchedAcquirers: IRestaurantBankSettingsAcquirer[];
	generatedWatchCommandsLink: Nullable<string>;
	useMenuVersion: TMenuContentVersion;
	useContentVersion: TMenuContentVersion;
	customerMenuVersion: string;
	customerContentVersion: string;
	restaurantMenuVersion: string;
	restaurantContentVersion: string;
	useTlsCertificate: boolean;
	tlsCertificateLevel: Nullable<TTlsCertificateLevel>;
	tlsCertificateName: Nullable<string>;
	tlsCertificateFilePath: Nullable<string>;
	tlsCertificateFileId: Nullable<string>;
	tlsCertificateFileHash: Nullable<string>;
	vatId: string;
	kmVatId: string;
	isActiveBpa: boolean;
	bpaSetId: Nullable<string>;
	isApplicableForVat: boolean;
	sendDeviceUnavailabilityAlerts: boolean;
	paymentTypeId: Nullable<string>;
	extraPaymentTypes: IExtraPaymentType[];
	dataForSubmit: IUpdateRestaurant | null;
	typeOfModalWindow: TTypeOfEditRestaurantModalWindow;
}

export const editRestaurantInitialState: IEditRestaurantState = {
	needRefetchData: false,
	initialFormData: null,
	visibleForm: '',
	restaurantId: '',
	name: '',
	shortName: '',
	fetchedCustomers: [],
	IDSelectedCustomer: null,
	clientType: null,
	fetchedCountries: [],
	IDSelectedCountry: null,
	fetchedCities: [],
	IDSelectedCity: null,
	address: '',
	fetchedCurrencies: [],
	selectedCurrencySymbol: null,
	masterPhoneNumber: '',
	email: '',
	code: '',
	additionalApplications: [],
	vivaWalletIsv: {
		sourceId: '',
		isvSourceId: '',
		isvPercent: null,
		isvFixedFeeAmount: null,
		isvFixedFeeThreshold: null,
		pinCode: '111111',
		franchiseFeeSupported: false,
		franchisePercent: null,
		franchiseFixedFeeAmount: null,
		franchiseFixedFeeThreshold: null,
	},
	priceType: null,
	supplier: {
		name: '',
		taxpayerId: '',
		phone: '',
	},
	isOpenPriceServiceCharge: false,
	openPricePercent: null,
	isUsedCustomerPriceType: false,
	loyalty: '',
	isUsedCustomerTips: false,
	tips0: null,
	tips1: null,
	tips2: null,
	tips3: null,
	defaultTips: null,
	tipsCommissionPercent: null,
	tipsCommissionMaxValue: null,
	isCharityEnabled: false,
	charityName: '',
	charityLogoPath: null,
	charityLogoFileId: null,
	charityAmount0: null,
	charityAmount1: null,
	charityAmount2: null,
	charityAmount3: null,
	defaultCharityAmountIndex: null,
	useKmTariff: false,
	isShowCustomerLoyaltyProgramButtons: false,
	isShowLoyaltyProgramButtons: false,
	baseMcc: null,
	mainMccCardBrand: null,
	mainMccCardMcc: null,
	mccCards: [],
	useCustomerMcc: false,
	downloadAppAndroidUrl: '',
	downloadAppIosUrl: '',
	fetchedLanguages: [],
	IDDefaultLanguage: null,
	IDsLanguages: [],
	restaurantLanguages: [],
	restrictionQrValue: '',
	usePosPhotos: false,
	enableStopListJob: false,
	useCustomerStopListSyncInterval: true,
	stopListSyncIntervalMinutes: null,
	updateOfTheLauncher: false,
	cardAcceptorId: '',
	cardAcceptorName: '',
	terminalId: '',
	vat: '',
	timeBlockChildAccess: '',
	IDPosSystem: null,
	fileIdForLauncherSettings: null,
	launcherSettingFilePath: null,
	fetchedAcquirers: [],
	generatedWatchCommandsLink: null,
	useMenuVersion: 'None',
	useContentVersion: 'None',
	customerMenuVersion: '',
	customerContentVersion: '',
	restaurantMenuVersion: '',
	restaurantContentVersion: '',
	useTlsCertificate: false,
	tlsCertificateLevel: null,
	tlsCertificateName: null,
	tlsCertificateFilePath: null,
	tlsCertificateFileId: null,
	tlsCertificateFileHash: null,
	vatId: '',
	kmVatId: '',
	isActiveBpa: false,
	bpaSetId: null,
	isApplicableForVat: false,
	sendDeviceUnavailabilityAlerts: true,
	paymentTypeId: null,
	extraPaymentTypes: [],
	dataForSubmit: null,
	typeOfModalWindow: null,
};

export interface IEditRestaurantContext extends IEditRestaurantState {
	refetchData(value: boolean): void;
	changeVisibleForm(value: TEditRestaurantVisibleForm): void;
	changeName(value: string): void;
	changeShortName(value: string): void;
	setFetchedCustomers(value: IItemForGetShortInfo[]): void;
	setIDCustomer(value: Nullable<string>): void;
	setClientType(value: Nullable<TTypeOfServiceProvided>): void;
	setFetchedCountries(value: IItemForGetShortInfo[]): void;
	setIDCountry(value: Nullable<string>): void;
	setFetchedCities(value: IItemForGetShortInfo[]): void;
	setIDCity(value: Nullable<string>): void;
	changeAddress(value: string): void;
	setFetchedCurrencies(value: ICurrencies[]): void;
	setCurrencySymbol(value: Nullable<string>): void;
	changeMasterPhoneNumber(value: string): void;
	changeEmail(value: string): void;
	changeCode(value: string): void;
	setAdditionalApplications(value: TAdditionalApplications[]): void;
	changeVivaWalletIsv({
		key,
		value,
	}: {
		key: keyof IEditRestaurantState['vivaWalletIsv'];
		value?: string | number | null;
	}): void;
	changeSupplier({ key, value }: { key: keyof IEditRestaurantState['supplier']; value: string }): void;
	toggleIsOpenPriceServiceCharge(): void;
	changeOpenPricePercent(value: Nullable<number>): void;
	changePriceType(value: Nullable<string>): void;
	toggleIsUsedCustomerPriceType(): void;
	changeLoyalty(value: string): void;
	toggleIsUsedCustomerTips(): void;
	setTips0(value: null | number): void;
	setTips1(value: null | number): void;
	setTips2(value: null | number): void;
	setTips3(value: null | number): void;
	setDefaultTips(value: TUseTipsByDefault): void;
	setTipsCommissionPercent(value: Nullable<number>): void;
	setTipsCommissionMaxValue(value: Nullable<number>): void;
	toggleIsCharityEnabled(): void;
	changeCharityName(value: string): void;
	setCharityLogoFile(fileId: Nullable<string>): void;
	setCharityAmount0(value: null | number): void;
	setCharityAmount1(value: null | number): void;
	setCharityAmount2(value: null | number): void;
	setCharityAmount3(value: null | number): void;
	setDefaultCharityAmountIndex(value: number): void;
	toggleUseKmTariff(): void;
	toggleShowLoyaltyProgramButtons(): void;
	changeBaseMcc(value: Nullable<number>): void;
	addMccCard(value: IFormMccCard): void;
	deleteMccCard(id: string): void;
	changeMainMccCardBrand(value: CardBrands): void;
	changeMccCardBrand(id: string, value: CardBrands): void;
	changeMainMccCardMcc(value: Nullable<number>): void;
	changeMccCardMcc(id: string, value: Nullable<number>): void;
	toggleUseCustomerMcc(): void;
	changeDownloadAppAndroidUrl(value: string): void;
	changeDownloadAppIosUrl(value: string): void;
	setFetchedLanguages(value: IItemForGetShortInfo[]): void;
	setIDDefaultLanguage(value: Nullable<string>): void;
	setIDsLanguages(value: string[]): void;
	changeRestrictionQrValue(value: string): void;
	toggleUsePosPhotos(): void;
	toggleStopListJob(): void;
	toggleUseCustomerStopListSyncInterval(): void;
	changeStopListSyncIntervalMinutes(value: Nullable<number>): void;
	toggleUpdateOfTheLauncher(): void;
	changeCardAcceptorId(value: string): void;
	changeCardAcceptorName(value: string): void;
	changeTerminalId(value: string): void;
	changeVat(value: string): void;
	changeTimeBlockChildAccess(value: string): void;
	setIDPosSystem(value: Nullable<TPosSystemId>): void;
	setFileIdForLauncherSettings(value: string): void;
	setFetchedAcquirers(value: IRestaurantBankSettingsAcquirer[]): void;
	toggleUseMenuVersion(value: TMenuContentVersion): void;
	toggleUseContentVersion(value: TMenuContentVersion): void;
	changeRestaurantMenuVersion(value: string): void;
	changeRestaurantContentVersion(value: string): void;
	toggleUseTLSCertificate(): void;
	setTlsCertificateLevel(value: Nullable<TTlsCertificateLevel>): void;
	changeTlsCertificateName(value: string): void;
	setTlsCertificateFile({ fileId, fileHash }: { fileId: Nullable<string>; fileHash: Nullable<string> }): void;
	changeVatId(value: string): void;
	changeKmVatId(value: string): void;
	setBpaSetId(value: Nullable<string>): void;
	toggleApplicableForVat(): void;
	toggleSendDeviceUnavailabilityAlerts(): void;
	changePaymentTypeId(value: string): void;
	addExtraPaymentType(value: IExtraPaymentType): void;
	changeExtraPaymentType(id: string, value: IExtraPaymentType): void;
	deleteExtraPaymentType(value: string): void;
	setDataForSubmit(data: IUpdateRestaurant | null): void;
	changeTypeOfModalWindow(value: TTypeOfEditRestaurantModalWindow): void;
	resetFields(): void;
}

export const EditRestaurantContext = createContext<IEditRestaurantContext>({
	refetchData: () => undefined,
	changeVisibleForm: () => undefined,
	changeName: () => undefined,
	changeShortName: () => undefined,
	setFetchedCustomers: () => undefined,
	setIDCustomer: () => undefined,
	setClientType: () => undefined,
	setFetchedCountries: () => undefined,
	setIDCountry: () => undefined,
	setFetchedCities: () => undefined,
	setIDCity: () => undefined,
	changeAddress: () => undefined,
	setFetchedCurrencies: () => undefined,
	setCurrencySymbol: () => undefined,
	changeMasterPhoneNumber: () => undefined,
	changeEmail: () => undefined,
	changeCode: () => undefined,
	setAdditionalApplications: () => undefined,
	changeVivaWalletIsv: () => undefined,
	changeSupplier: () => undefined,
	toggleIsOpenPriceServiceCharge: () => undefined,
	changeOpenPricePercent: () => undefined,
	changePriceType: () => undefined,
	toggleIsUsedCustomerPriceType: () => undefined,
	changeLoyalty: () => undefined,
	toggleIsUsedCustomerTips: () => undefined,
	setTips0: () => undefined,
	setTips1: () => undefined,
	setTips2: () => undefined,
	setTips3: () => undefined,
	setDefaultTips: () => undefined,
	setTipsCommissionPercent: () => undefined,
	setTipsCommissionMaxValue: () => undefined,
	toggleIsCharityEnabled: () => undefined,
	changeCharityName: () => undefined,
	setCharityLogoFile: () => undefined,
	setCharityAmount0: () => undefined,
	setCharityAmount1: () => undefined,
	setCharityAmount2: () => undefined,
	setCharityAmount3: () => undefined,
	setDefaultCharityAmountIndex: () => undefined,
	toggleUseKmTariff: () => undefined,
	toggleShowLoyaltyProgramButtons: () => undefined,
	changeBaseMcc: () => undefined,
	addMccCard: () => undefined,
	deleteMccCard: () => undefined,
	changeMainMccCardBrand: () => undefined,
	changeMccCardBrand: () => undefined,
	changeMainMccCardMcc: () => undefined,
	changeMccCardMcc: () => undefined,
	toggleUseCustomerMcc: () => undefined,
	changeDownloadAppAndroidUrl: () => undefined,
	changeDownloadAppIosUrl: () => undefined,
	setFetchedLanguages: () => undefined,
	setIDDefaultLanguage: () => undefined,
	setIDsLanguages: () => undefined,
	changeRestrictionQrValue: () => undefined,
	toggleUsePosPhotos: () => undefined,
	toggleStopListJob: () => undefined,
	toggleUseCustomerStopListSyncInterval: () => undefined,
	changeStopListSyncIntervalMinutes: () => undefined,
	toggleUpdateOfTheLauncher: () => undefined,
	changeCardAcceptorId: () => undefined,
	changeCardAcceptorName: () => undefined,
	changeTerminalId: () => undefined,
	changeVat: () => undefined,
	changeTimeBlockChildAccess: () => undefined,
	setIDPosSystem: () => undefined,
	setFileIdForLauncherSettings: () => undefined,
	setFetchedAcquirers: () => undefined,
	toggleUseMenuVersion: () => undefined,
	toggleUseContentVersion: () => undefined,
	changeRestaurantMenuVersion: () => undefined,
	changeRestaurantContentVersion: () => undefined,
	toggleUseTLSCertificate: () => undefined,
	setTlsCertificateLevel: () => undefined,
	changeTlsCertificateName: () => undefined,
	setTlsCertificateFile: () => undefined,
	changeVatId: () => undefined,
	changeKmVatId: () => undefined,
	setBpaSetId: () => undefined,
	toggleApplicableForVat: () => undefined,
	toggleSendDeviceUnavailabilityAlerts: () => undefined,
	changePaymentTypeId: () => undefined,
	addExtraPaymentType: () => undefined,
	changeExtraPaymentType: () => undefined,
	deleteExtraPaymentType: () => undefined,
	setDataForSubmit: () => undefined,
	changeTypeOfModalWindow: () => undefined,
	resetFields: () => undefined,
	...editRestaurantInitialState,
});
